import { useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { getTelegramToken, getUserProfile } from 'src/api';
import { useBoolean } from 'src/hooks/use-boolean';
import { isSuccessResponse } from 'src/utils/response-type-guards';
import { telegramProfileKeys } from 'src/utils/query-keys';

export type TelegramStatus = 'idle' | 'loading' | 'connected' | 'error';

export const useTelegramConnect = () => {
  const { value: isPolling, onTrue: startPolling, onFalse: stopPolling } = useBoolean();
  const { value: hasError, onTrue: showError, onFalse: hideError } = useBoolean();
  const pendingWindowRef = useRef<Window | null>(null);

  const { data: profileData } = useQuery({
    queryKey: telegramProfileKeys.detail(),
    queryFn: getUserProfile,
    refetchInterval: isPolling ? 3000 : false,
  });

  const profile = profileData && isSuccessResponse(profileData) ? profileData : null;
  const isConnected = Boolean(profile?.telegram_connected);

  useEffect(() => {
    if (isPolling && isConnected) {
      stopPolling();
    }
  }, [isPolling, isConnected, stopPolling]);

  const { mutate: fetchToken, isPending } = useMutation({
    mutationFn: getTelegramToken,
    onSuccess: (data) => {
      if (!isSuccessResponse(data) || !data.bot_link) {
        pendingWindowRef.current?.close();
        pendingWindowRef.current = null;
        showError();
        return;
      }
      if (pendingWindowRef.current) {
        pendingWindowRef.current.location.href = data.bot_link;
        pendingWindowRef.current = null;
      }
      startPolling();
      hideError();
    },
    onError: () => {
      pendingWindowRef.current?.close();
      pendingWindowRef.current = null;
      showError();
    },
  });

  const connect = () => {
    hideError();
    // Safari only allows window.open() synchronously within a user gesture.
    // Open a blank window now, then redirect it once the token arrives.
    pendingWindowRef.current = window.open('', '_blank');
    fetchToken();
  };

  const status: TelegramStatus = (() => {
    if (!profile) return 'loading';
    if (isConnected) return 'connected';
    if (isPending) return 'loading';
    if (hasError) return 'error';
    return 'idle';
  })();

  return { status, connect };
};
